import { useState, useMemo, useCallback, useEffect } from "react";
import { LibraryBrowser } from "@/ui-components/LibraryBrowser";
import { portfolioToLibrarySections } from "@/ui-components/LibraryBrowser/adapters";
import type { LibraryItem, DetailPaneCallbacks } from "@/ui-components/LibraryBrowser";
import { usePortfoliosCRUD } from "../api/usePortfoliosCRUD";
import { useHeadlineQueriesCRUD } from "@/widget-components/newsquawk-headlines/api/headline-query/useHeadlineQueriesCRUD";
import { useTagsWatchlistsCRUD } from "@/shared/api/pref-storage/tags-watchlists/useTagsWatchlistsCRUD";
import { menuCategories } from "@/widget-components/newsquawk-headlines/models/categories";
import { getTagsByIds } from "@/shared/api/newsquawk-headlines";
import {
  setPrebuiltOverride,
  clearPrebuiltOverride,
  isPrebuiltWatchlist,
} from "../prebuiltWatchlists";
import { usePortfolioBuilder } from "./PortfolioBuilderContext";
import ViewAdvancedFilterModal from "../ViewAdvancedFilterModal";
import FilterBuilderBrowser from "./FilterBuilderBrowser";
import WatchlistCreatorBrowser from "./WatchlistCreatorBrowser";
import type { WatchlistCreatorMode } from "../hooks/useWatchlistCreator";
import type { Portfolio } from "@/types/Portfolio";
import type { TagsWatchlist } from "@/shared/api/pref-storage/tags-watchlists";
import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import { getWatchlistType } from "@/widget-components/firehose-widgets/shared/hooks/searchUtils";
import useAuth from "@/hooks/useAuth";

// Static seed from menuCategories
const menuTagIdToName: Record<string, string> = {};
menuCategories.forEach((cat) => {
  cat.items.forEach((item) => {
    menuTagIdToName[String(item.id)] = item.title;
  });
});

export default function PortfolioBuilderBrowser() {
  const { portfolios, updateMutation } = usePortfoliosCRUD();
  const { queriesQuery } = useHeadlineQueriesCRUD();
  const { watchlistsQuery } = useTagsWatchlistsCRUD();
  const { setPreviewWatchlist, setPreviewFilter, setPreviewTagWatchlist } = usePortfolioBuilder();
  const { authenticated } = useAuth();

  // Dynamic tag name resolution
  const [resolvedTagNames, setResolvedTagNames] = useState<Record<string, string>>({});

  const tagIdToName = useMemo(
    () => ({ ...menuTagIdToName, ...resolvedTagNames }),
    [resolvedTagNames]
  );

  // Fetch names for tag IDs not in menuCategories
  useEffect(() => {
    const allTagIds = (watchlistsQuery.data || []).flatMap((tw) => tw.tags);
    const unknownIds = allTagIds.filter((id) => !menuTagIdToName[id] && !resolvedTagNames[id]);
    if (unknownIds.length === 0) return;

    const uniqueIds = [...new Set(unknownIds)];
    getTagsByIds(uniqueIds.join(" "))
      .then((tagDetails) => {
        const newNames: Record<string, string> = {};
        if (tagDetails) {
          for (const [tagId, detail] of Object.entries(tagDetails)) {
            const primary = detail.labels?.find((l) => l.primary);
            const name = primary?.name || detail.labels?.[0]?.name;
            if (name) newNames[tagId] = name;
          }
        }
        if (Object.keys(newNames).length > 0) {
          setResolvedTagNames((prev) => ({ ...prev, ...newNames }));
        }
      })
      .catch(() => {});
  }, [watchlistsQuery.data]);

  // Creator state
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [creatorMode, setCreatorMode] = useState<WatchlistCreatorMode>("ticker");
  const [editingPortfolio, setEditingPortfolio] = useState<Portfolio | null>(null);

  // Advanced filter state
  const [advancedFilterModalOpen, setAdvancedFilterModalOpen] = useState(false);
  const [editingFilter, setEditingFilter] = useState<CategoryQuery | null>(null);
  const [viewingFilter, setViewingFilter] = useState<CategoryQuery | null>(null);

  // Build sections from data
  const sections = useMemo(() => {
    const tagWatchlists = watchlistsQuery.data || [];
    const filters = queriesQuery.data || [];

    return portfolioToLibrarySections(portfolios, tagWatchlists, filters, {
      tagIdToName,
      onCreateTicker: () => {
        setCreatorMode("ticker");
        setCreatorOpen(true);
      },
      onCreateKeyword: () => {
        setCreatorMode("keyword");
        setCreatorOpen(true);
      },
      onCreateTag: () => {
        setCreatorMode("tag");
        setCreatorOpen(true);
      },
      onCreateAdvanced: () => {
        setEditingFilter(null);
        setAdvancedFilterModalOpen(true);
      },
    });
  }, [portfolios, watchlistsQuery.data, queriesQuery.data]);

  // Handle item click → preview
  const handleItemClick = useCallback(
    (item: LibraryItem) => {
      const meta = item.metadata ?? {};
      const itemType = meta.itemType as string;

      if (itemType === "ticker" || itemType === "keyword") {
        const portfolioId = meta.portfolioId as number;
        setPreviewWatchlist(portfolioId);
      } else if (itemType === "tag") {
        const tagWatchlistId = meta.tagWatchlistId as number;
        const tags = (meta.tags as { id: string }[]) ?? [];
        setPreviewTagWatchlist(tagWatchlistId, tags.map((t) => t.id));
      } else if (itemType === "advanced-filter") {
        const filterId = meta.filterId as number;
        setPreviewFilter(filterId);
      }
    },
    [setPreviewWatchlist, setPreviewFilter, setPreviewTagWatchlist]
  );

  // Handle item edit — opens the appropriate editor for each item type
  const handleItemEdit = useCallback(
    (item: LibraryItem) => {
      const meta = item.metadata ?? {};
      const itemType = meta.itemType as string;
      const ownerType = meta.ownerType as string;

      if (itemType === "advanced-filter") {
        const filterId = meta.filterId as number;
        const filter = (queriesQuery.data || []).find((q) => q.id === filterId);
        if (filter) {
          if (ownerType === "NEWSQUAWK") {
            setViewingFilter(filter);
          } else {
            setEditingFilter(filter);
            setAdvancedFilterModalOpen(true);
          }
        }
      } else if (itemType === "ticker" || itemType === "keyword") {
        const portfolioId = meta.portfolioId as number;
        const portfolio = portfolios.find((p) => p.id === portfolioId);
        if (portfolio && ownerType === "USER") {
          const isKeywordOnly = (portfolio.keywords?.length ?? 0) > 0 && portfolio.tickers.length === 0;
          setCreatorMode(isKeywordOnly ? "keyword" : "ticker");
          setEditingPortfolio(portfolio);
          setCreatorOpen(true);
        }
      } else if (itemType === "tag") {
        const tagWatchlistId = meta.tagWatchlistId as number;
        if (tagWatchlistId && ownerType === "USER") {
          // Tag editing — for now open the creator in tag mode
          // The tag watchlist data needs to be converted to a Portfolio-like shape
          setCreatorMode("tag");
          setCreatorOpen(true);
        }
      }
    },
    [portfolios, queriesQuery.data]
  );

  // Detail pane callbacks
  const detailCallbacks = useMemo<DetailPaneCallbacks>(() => ({
    onEditColor: (item, color) => {
      const meta = item.metadata ?? {};
      const portfolioId = meta.portfolioId as number | undefined;
      if (!portfolioId) return;

      const portfolio = portfolios.find((p) => p.id === portfolioId);
      if (!portfolio) return;

      if (isPrebuiltWatchlist(portfolio)) {
        setPrebuiltOverride(portfolioId, { color });
      } else {
        updateMutation.mutate({ ...portfolio, color });
      }
    },
    onEditPriority: (item, priority) => {
      const meta = item.metadata ?? {};
      const portfolioId = meta.portfolioId as number | undefined;
      if (!portfolioId) return;

      const portfolio = portfolios.find((p) => p.id === portfolioId);
      if (!portfolio) return;

      if (isPrebuiltWatchlist(portfolio)) {
        setPrebuiltOverride(portfolioId, { priority });
      } else {
        updateMutation.mutate({ ...portfolio, priority });
      }
    },
    onEdit: (item) => handleItemEdit(item),
    onResetDefaults: (item) => {
      const meta = item.metadata ?? {};
      const portfolioId = meta.portfolioId as number | undefined;
      if (portfolioId) {
        clearPrebuiltOverride(portfolioId);
      }
    },
  }), [portfolios, updateMutation, handleItemEdit]);

  return (
    <>
      <LibraryBrowser
        mode="browse"
        sections={sections}
        onPreview={handleItemClick}
        onItemEdit={handleItemEdit}
        detailCallbacks={detailCallbacks}
        className="flex-1 min-h-0"
      />

      {/* Creator browser for new/editing watchlists */}
      <WatchlistCreatorBrowser
        isOpen={creatorOpen}
        onClose={() => { setCreatorOpen(false); setEditingPortfolio(null); }}
        mode={creatorMode}
        editingPortfolio={editingPortfolio}
      />

      <FilterBuilderBrowser
        isOpen={advancedFilterModalOpen}
        onClose={() => { setAdvancedFilterModalOpen(false); setEditingFilter(null); }}
        queryId={editingFilter?.id}
      />

      <ViewAdvancedFilterModal
        isOpen={!!viewingFilter}
        onClose={() => setViewingFilter(null)}
        filter={viewingFilter}
      />
    </>
  );
}
